import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { getItemsApi, deleteData } from "../../services/apiServices";
import { Spinner, Button, Container, Badge, Row, Col } from "reactstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const Items = () => {
  const [pending, setPending] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const fetchItems = async (page = 1, limit = 10) => {
    try {
      setPending(true);
      const response = await getItemsApi(page, limit);

      if (response.status && response.data) {
        setRows(response.data.items || []); // Changed from packs to items
        setTotalRows(response.data.pagination?.total || 0);
      } else {
        setError("Invalid API response structure");
      }
    } catch (err) {
      setError("Failed to fetch items");
      toast.error("Failed to fetch items");
    } finally {
      setPending(false);
    }
  };

  useEffect(() => {
    fetchItems(page, perPage);
  }, [page, perPage]);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await deleteData(`api/v2/admin/auth/items/${id}`);
      toast.success("Item deleted successfully");
      fetchItems(page, perPage);
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    );
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "80px",
      cell: (row, index) => <div className="text-center">{index + 1}</div>,
    },
    {
      name: "Image",
      selector: (row) => row.image,
      sortable: false,
      width: "100px",
      cell: (row) => (
        <div className="text-center">
          <img
            src={row.image}
            alt={row.name}
            className="rounded"
            style={{ width: "60px", height: "60px", objectFit: "cover" }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/60x60?text=No+Image";
            }}
          />
        </div>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => (
        <div>
          <strong>{row.name}</strong>
          <div className="text-muted small">
            ID: {row._id.substring(0, 8)}...
          </div>
        </div>
      ),
      minWidth: "200px",
    },
    {
      name: "Description",
      selector: (row) => row.description,
      sortable: false,
      cell: (row) => (
        <div
          className="text-truncate"
          style={{ maxWidth: "300px" }}
          title={row.description}>
          {row.description?.substring(0, 100) || "No description"}...
        </div>
      ),
      minWidth: "300px",
    },
    {
      name: "Value",
      selector: (row) => row.amount,
      sortable: true,
      cell: (row) => (
        <div className="fw-bold text-success">{formatCurrency(row.amount)}</div>
      ),
      minWidth: "120px",
    },
    {
      name: "Created",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => <div className="small">{formatDate(row.createdAt)}</div>,
      minWidth: "180px",
    },
    {
      name: "Updated",
      selector: (row) => row.updatedAt,
      sortable: true,
      cell: (row) => <div className="small">{formatDate(row.updatedAt)}</div>,
      minWidth: "180px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <Button
            size="sm"
            color="info"
            onClick={() => navigate(`/items/view/${row._id}`)}
            title="View Item Details"
            className="mb-1">
            <i className="ri-eye-line" />
          </Button>

          <Button
            size="sm"
            color="warning"
            onClick={() => navigate(`/items/edit/${row._id}`)}
            title="Edit Item"
            className="mb-1">
            <i className="ri-edit-line" />
          </Button>

          <Button
            size="sm"
            color="danger"
            onClick={() => handleDelete(row._id)}
            title="Delete Item"
            className="mb-1">
            <i className="ri-delete-bin-line" />
          </Button>
        </div>
      ),
      minWidth: "180px",
      ignoreRowClick: true,
      button: true,
    },
  ];

  const customStyles = {
    table: {
      style: {
        width: "100%",
      },
    },
    tableWrapper: {
      style: {
        display: "table",
        width: "100%",
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f8f9fa",
        fontWeight: "bold",
        fontSize: "14px",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
        paddingLeft: "16px",
        paddingRight: "16px",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
  };

  if (error) return <div className="alert alert-danger">{error}</div>;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PackDraw" breadcrumbItem="All Items" />

          <Row className="align-items-center mb-3">
            <Col>
              <h4 className="mb-0">Items Management</h4>
              <p className="text-muted mb-0">
                Total {totalRows} items available
              </p>
            </Col>
            <Col className="text-end">
              <Button
                color="primary"
                onClick={() => navigate("/items/create")}
                className="d-inline-flex align-items-center gap-2">
                <i className="ri-add-line"></i>
                Add New Item
              </Button>
            </Col>
          </Row>

          {/* Full width DataTable */}
          <div className="w-100 overflow-auto">
            <DataTable
              columns={columns}
              data={rows}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationDefaultPage={page}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              progressPending={pending}
              progressComponent={
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              }
              highlightOnHover
              responsive={false}
              customStyles={customStyles}
              noDataComponent={
                <div className="text-center py-5">
                  <i className="ri-inbox-line display-4 text-muted"></i>
                  <h5 className="mt-3">No items found</h5>
                  <p className="text-muted">
                    Start by creating your first item
                  </p>
                </div>
              }
              className="w-100"
            />
          </div>

          {/* Stats Summary - Full width cards */}
          <Row className="mt-4 g-3">
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Total Items</p>
                      <h4 className="mb-0">{totalRows}</h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-4">
                        <i className="ri-box-3-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Total Value</p>
                      <h4 className="mb-0">
                        {formatCurrency(
                          rows.reduce(
                            (sum, item) => sum + (item.amount || 0),
                            0,
                          ),
                        )}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-4">
                        <i className="ri-money-dollar-circle-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Average Value</p>
                      <h4 className="mb-0">
                        {rows.length > 0
                          ? formatCurrency(
                              rows.reduce(
                                (sum, item) => sum + (item.amount || 0),
                                0,
                              ) / rows.length,
                            )
                          : formatCurrency(0)}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-4">
                        <i className="ri-calculator-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
            <Col xl={3} lg={6}>
              <div className="card card-animate">
                <div className="card-body">
                  <div className="d-flex justify-content-between">
                    <div>
                      <p className="text-muted mb-2">Last Updated</p>
                      <h4 className="mb-0">
                        {rows.length > 0
                          ? new Date(
                              Math.max(
                                ...rows.map((item) => new Date(item.updatedAt)),
                              ),
                            ).toLocaleDateString()
                          : "N/A"}
                      </h4>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle text-warning rounded-circle fs-4">
                        <i className="ri-history-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Items;
